import { useMemo, useState } from "react";
import type { BaseTableProps, SortDirection } from "./types";

export const useBaseTableHooks = <T extends { id: number }>({
  rows: data,
}: BaseTableProps<T>) => {
  const [sortColumn, setSortColumn] = useState<keyof T | null>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [filters, setFilters] = useState<Partial<Record<keyof T, string>>>({});

  const handleSort = (column: keyof T) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  const handleFilter = (column: keyof T, value: string) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // Filter 機能を適用
    for (const [key, value] of Object.entries(filters)) {
      if (value) {
        result = result.filter((item) =>
          String(item[key as keyof T])
            .toLowerCase()
            .includes(String(value).toLowerCase()),
        );
      }
    }

    // Sort 機能を適用
    const sortColumnOrId = sortColumn || "id";
    result.sort((a, b) => {
      if (a[sortColumnOrId] < b[sortColumnOrId])
        return sortDirection === "asc" ? -1 : 1;
      if (a[sortColumnOrId] > b[sortColumnOrId])
        return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [data, filters, sortColumn, sortDirection]);

  return {
    sortColumn,
    sortDirection,
    filters,
    setSortColumn,
    setSortDirection,
    setFilters,
    handleSort,
    handleFilter,
    filteredAndSortedData,
  };
};
